import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { fetchGlobalSearch, type GlobalSearchData } from './api';
import {
  GlobalSearchLoading,
  GlobalSearchPlaceholderDesktop,
  GlobalSearchResultsView,
  type GlobalFilter,
} from './GlobalSearchResults';
import { useDebouncedValue } from './useDebouncedValue';

type QueryValue =
  | { kind: 'ok'; data: GlobalSearchData }
  | { kind: 'miss'; message: string };

export function DesktopGlobalSearchMount(): ReactElement {
  const [raw, setRaw] = useState('');
  const debounced = useDebouncedValue(raw.trim(), 800);
  const [activeFilter, setActiveFilter] = useState<GlobalFilter>('all');
  const resultsHostRef = useRef<HTMLDivElement>(null);

  const enabled = debounced.length >= 2;

  const q = useQuery({
    queryKey: ['rnm', 'global', 'desktop', debounced],
    queryFn: async (): Promise<QueryValue> => {
      const r = await fetchGlobalSearch(debounced, { reloadOnPersistentNonce: false });
      if (r.ok) {
        return { kind: 'ok', data: r.data };
      }
      return { kind: 'miss', message: r.message };
    },
    enabled,
  });

  useEffect(() => {
    function onClosed(): void {
      setRaw('');
      setActiveFilter('all');
    }
    function onOpen(): void {
      setTimeout(() => document.getElementById('rnm-desktop-search-input')?.focus(), 100);
    }
    window.addEventListener('rnm-desktop-dropdown-closed', onClosed);
    window.addEventListener('rnm-desktop-dropdown-opened', onOpen);
    return () => {
      window.removeEventListener('rnm-desktop-dropdown-closed', onClosed);
      window.removeEventListener('rnm-desktop-dropdown-opened', onOpen);
    };
  }, []);

  const getActiveResultIndex = useCallback((): number => {
    const root = resultsHostRef.current;
    if (!root) {
      return -1;
    }
    const items = root.querySelectorAll('a.rnm-search-product, a.rnm-search-post, a.rnm-search-page');
    for (let i = 0; i < items.length; i++) {
      if (items[i].classList.contains('rnm-result-active')) {
        return i;
      }
    }
    return -1;
  }, []);

  const setActiveResult = useCallback((index: number) => {
    const root = resultsHostRef.current;
    if (!root) {
      return;
    }
    const items = root.querySelectorAll('a.rnm-search-product, a.rnm-search-post, a.rnm-search-page');
    items.forEach((el) => el.classList.remove('rnm-result-active'));
    if (index >= 0 && index < items.length) {
      items[index].classList.add('rnm-result-active');
      items[index].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    const root = resultsHostRef.current;
    if (!root) {
      return;
    }
    const items = root.querySelectorAll('a.rnm-search-product, a.rnm-search-post, a.rnm-search-page');
    items.forEach((el) => el.classList.remove('rnm-result-active'));
  }, [debounced, q.data]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const root = resultsHostRef.current;
      if (!root) {
        return;
      }
      const items = root.querySelectorAll('a.rnm-search-product, a.rnm-search-post, a.rnm-search-page');
      if (items.length === 0) {
        return;
      }

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const cur = getActiveResultIndex();
          const next = cur < 0 ? 0 : (cur + 1) % items.length;
          setActiveResult(next);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const cur = getActiveResultIndex();
          const next = cur < 0 ? items.length - 1 : (cur - 1 + items.length) % items.length;
          setActiveResult(next);
          break;
        }
        case 'Enter': {
          e.preventDefault();
          const active = root.querySelector(
            'a.rnm-search-product.rnm-result-active, a.rnm-search-post.rnm-result-active, a.rnm-search-page.rnm-result-active'
          ) as HTMLAnchorElement | null;
          const hrefActive = active?.getAttribute('href');
          if (hrefActive) {
            window.location.href = hrefActive;
            break;
          }
          if (items.length === 1) {
            const href = (items[0] as HTMLAnchorElement).getAttribute('href');
            if (href) {
              window.location.href = href;
            }
          }
          break;
        }
        case 'Escape':
          e.preventDefault();
          window.dispatchEvent(new CustomEvent('rnm-desktop-dropdown-request-close'));
          break;
        default:
          break;
      }
    },
    [getActiveResultIndex, setActiveResult]
  );

  let results: ReactElement;
  if (!enabled) {
    results = <GlobalSearchPlaceholderDesktop />;
  } else if (q.isFetching && !q.data) {
    results = <GlobalSearchLoading />;
  } else if (q.isError) {
    results = (
      <div className="rnm-error">
        <p>Error al buscar. Intenta nuevamente.</p>
      </div>
    );
  } else if (q.data?.kind === 'miss') {
    results = (
      <div className="rnm-no-results">
        <p dangerouslySetInnerHTML={{ __html: q.data.message }} />
      </div>
    );
  } else if (q.data?.kind === 'ok') {
    results = (
      <GlobalSearchResultsView
        data={q.data.data}
        activeFilter={activeFilter}
        onFilterChange={(f) => {
          setActiveFilter(f);
        }}
      />
    );
  } else {
    results = <GlobalSearchLoading />;
  }

  return (
    <>
      <div className="rnm-dropdown-header">
        <input
          type="text"
          id="rnm-desktop-search-input"
          className="rnm-desktop-search-input"
          placeholder="Buscar repuestos, accesorios, marcas..."
          autoComplete="off"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onClick={(ev) => ev.stopPropagation()}
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          className="rnm-dropdown-close"
          id="rnm-desktop-search-close"
          aria-label="Cerrar"
        >
          &times;
        </button>
      </div>
      <div id="rnm-desktop-search-results" className="rnm-desktop-search-results" ref={resultsHostRef}>
        {results}
      </div>
    </>
  );
}
